import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { productPath, productVariantEditPath } from "../../../../products/urls";
import { ReportsUrl } from "../../../overview/urls";
import { InvalidThreeMonth } from "../../../utils/misc";
import { reportsDownload } from "../../../utils/utils";
import ProductStocksListPage from "../../components/ProductStockPage";
import {
  useInitialProductStockFilterDataQuery,
  useProductStocksDownload,
  useProductStocksQuery
} from "../../queries";
import {
  ProductStocksListUrl,
  ProductStocksListUrlDialog,
  ProductStocksListUrlQueryParams
} from "../../url";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface ProductStocksListProps {
  params: ProductStocksListUrlQueryParams;
}

export const ProductStocksList: React.FC<ProductStocksListProps> = ({
  params
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();

  const intl = useIntl();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");

  const sheetName = "Product Stocks";
  const fileName = `${reportsDownload.ProductStocks.filename}_${dateNow}.xlsx`;
  const currencySymbol = shop?.defaultCurrency || "PHP";

  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const { updateListSettings, settings } = useListSettings(
    ListViews.PRODUCT_STOCKS
  );

  const paginationState = createPaginationState(settings.rowNumber, params);
  React.useEffect(() => {
    if (
      InvalidThreeMonth(params.updateFrom, params.updateTo) ||
      InvalidThreeMonth(params.stockUpdateFrom, params.stockUpdateTo)
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(ProductStocksListUrl());
    }
  }, [params.updateTo]);

  React.useEffect(() => {
    if (
      params.productStatus ||
      params.updateFrom ||
      params.query === "" ||
      params.trackInventory
    ) {
      if (
        params?.brand === undefined &&
        params?.mall === undefined &&
        params.vertical === undefined
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "Please select a filter for Mall, Brand or Vertical."
          })
        });
        navigate(ProductStocksListUrl());
      }
    }
  }, [
    params.productStatus,
    params.updateFrom,
    params.query,
    params.trackInventory
  ]);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const reportVariables = React.useMemo(
    () => ({
      ...paginationState,
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1500
    }),
    [params]
  );

  const { data: initialFilterData } = useInitialProductStockFilterDataQuery({
    variables: {
      brand: params.brand,
      mall: params.mall
    }
  });

  React.useEffect(
    () =>
      navigate(
        ProductStocksListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const searchBrand = useBrandSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });
  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: ProductStocksListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    ProductStocksListUrlDialog,
    ProductStocksListUrlQueryParams
  >(navigate, ProductStocksListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      ProductStocksListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(ProductStocksListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { data: previewData } = useProductStocksQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const { data: reportData, loading: reportLoading } = useProductStocksDownload(
    {
      displayLoader: true,
      skip: loadData,
      variables: reportVariables
    }
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.productStocks.pageInfo,
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(
    params,
    {
      initial: maybe(
        () => initialFilterData.brands.edges.map(edge => edge.node) || []
      ),
      search: searchBrand
    },
    {
      initial: maybe(
        () => initialFilterData.malls.edges.map(edge => edge.node) || []
      ),
      search: searchMall
    }
  );

  const selectReportData = reportData => {
    const selectedData = reportData?.map(data => {
      delete data.__typename;
      delete data.id;
      delete data.is_simple_product;
      delete data.variant_id;
      data.updated_at = data?.updated_at
        ? Moment.tz(new Date(data?.updated_at), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;

      data.stock_updated_at = data?.stock_updated_at
        ? Moment.tz(new Date(data?.stock_updated_at), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;
      data.is_visible = data.is_visible ? data.is_visible : false;
      data.is_available = data.is_available ? data.is_available : false;

      data.last_published_date = data?.last_published_date
        ? Moment.tz(new Date(data?.last_published_date), "Asia/Manila").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : null;

      data.last_unpublished_date = data?.last_unpublished_date
        ? Moment.tz(
            new Date(data?.last_unpublished_date),
            "Asia/Manila"
          ).format("YYYY-MM-DD HH:mm:ss")
        : null;

      return data;
    });
    return selectedData;
  };

  const [downloadData, setDownloadData] = useState([]);

  const shownproductStocks = previewData?.productStocks?.edges.map(
    edge => edge.node
  );
  const allproductStocks = reportData?.productStocks?.edges.map(
    edge => edge.node
  );
  const nextPage = reportData?.productStocks?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allproductStocks]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());

        // added visibility in data array
        const addVisibility = selectedReportData.map(e => ({
          visibility: e.is_visible,
          ...e
        }));

        useDownloadReport(
          addVisibility,
          reportsDownload.ProductStocks.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Product Stocks successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  return (
    <>
      <ProductStocksListPage
        productStocks={shownproductStocks}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={reportLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        download={() => openModal("download")}
        onRowClick={id => () => window.open(productPath(id))}
        onProductStockRowClick={(id, variantId) => () =>
          window.open(productVariantEditPath(id, variantId))}
      />
      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Product Stocks",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a product stocks with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{previewData?.productStocks?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default ProductStocksList;
